import React, { Component } from "react";
import { Link, Redirect } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink
} from '@mui/material';
import { signin, authenticate, signinZendesk } from "../auth";
import SBSLogo from '../images/sbs-logo-130x50.png';
import ReactGA from "react-ga";

class Signin extends Component {
  constructor() {
    super();
    this.state = {
      email: "",
      rememberMe: false,
      password: "",
      error: "",
      redirectToReferer: false,
      redirectToChangePassword: false,
      loading: false,
      recaptcha: false,
      show: false,
      SBSEmail: '',
      SBSPassword: '',
      success: false
    };
  }

  handleClose = () => this.setState({ show: false });
  handleShow = () => this.setState({ show: true });

  handleChange = name => event => {
    const input = event.target;
    const value = event.target.type === 'checkbox' ? input.checked : input.value;

    this.setState({ error: "" });
    this.setState({ [name]: value });
  };

  recaptchaRef = React.createRef();

  onRecaptchaChange = (token) => {
    this.setState({ recaptcha: !!token, recaptchaToken: token || "" });
  };

  onRecaptchaError = () => {
    this.setState({ recaptchaError: true });
  };

  componentDidMount() {
    this.setState({ email: '', rememberMe: false });
  };

  clickSubmit = event => {
    event.preventDefault();
    this.setState({ loading: true });
    const { email, password, recaptchaToken } = this.state;

    if (!this.state.recaptcha) {
      this.setState({
        loading: false,
        error: "Please complete the reCAPTCHA verification."
      });
      return;
    }

    const user = {
      email: email.toLowerCase(),
      password,
      recaptchaToken
    };

    signin(user).then(data => {
      if (data.error) {
        this.setState({ error: data.error, loading: false });
        // Reset reCAPTCHA on failed attempt
        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
        this.setState({ recaptcha: false, recaptchaToken: "" });
      } else {
        localStorage.removeItem('user');

        authenticate(data, () => {
          if (data.user && data.user.mustChangePassword) {
            this.setState({ redirectToChangePassword: true });
          } else {
            this.setState({ redirectToReferer: true });
          }
        });
      }
    }).catch(err => {
      console.error('Signin error:', err);
      this.setState({ error: 'Unable to connect to server. Please try again.', loading: false });
    });
  };

  launchZendesk = e => {
    e.preventDefault();
    ReactGA.event({
      category: 'Form',
      action: 'Form Submit',
      transport: 'beacon'
    });
    this.setState({ error: "" })
    this.setState({ success: false });

    const body = {
      username: this.state.SBSEmail,
      password: this.state.SBSPassword
    }
    signinZendesk({ body }).then(data => {
      if (data.error) {
        this.setState({ error: data.error })
        this.handleClose();
      } else {
        authenticate(data, () => {
          this.setState({ redirectToReferer: true });
        });
        this.handleClose();
      }
    });
  };

  render() {
    const {
      email,
      password,
      rememberMe,
      error,
      redirectToReferer,
      redirectToChangePassword,
      loading,
      recaptcha
    } = this.state;

    if (redirectToChangePassword) {
      return <Redirect to="/change-password" />;
    }

    if (redirectToReferer) {
      return <Redirect to="/usershome" />;
    }

    return (
      <Container maxWidth="sm" sx={{ mt: 5, mb: 5 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
            Sign In
          </Typography>

          {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('expired') === '1' && !error && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Your session has expired. Please sign in again to continue.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Loading...
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={this.clickSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={this.handleChange("email")}
                margin="normal"
                variant="outlined"
                required
                autoComplete="email"
                inputProps={{ style: { fontSize: '16px' } }}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={this.handleChange("password")}
                margin="normal"
                variant="outlined"
                required
                autoComplete="current-password"
                inputProps={{ style: { fontSize: '16px' } }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2, '& > div': { maxWidth: '100%', overflow: 'hidden' } }}>
                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={this.onRecaptchaChange}
                  onErrored={this.onRecaptchaError}
                  size={window.innerWidth < 400 ? 'compact' : 'normal'}
                />
                {this.state.recaptchaError && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    reCAPTCHA failed to load. Please disable content blockers or try a different browser.
                  </Alert>
                )}
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={this.handleChange("rememberMe")}
                    name="rememberMe"
                  />
                }
                label="Remember me"
                sx={{ mt: 1, mb: 2 }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 2, mb: 2 }}
              >
                Submit
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <MuiLink component={Link} to="/forgot-password" underline="hover">
                  Forgot Password?
                </MuiLink>
              </Box>

              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Need an account? Sign up here. It's free!
                </Typography>
                <Button
                  fullWidth
                  component="a"
                  href="/signup"
                  variant="outlined"
                  color="secondary"
                  size="large"
                >
                  Sign up for an SDSIC account
                </Button>
              </Box>

              {/* <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="text"
                  color="primary"
                  onClick={this.handleShow}
                  size="small"
                >
                  Login with SBS
                </Button>
              </Box> */}
            </Box>
          )}
        </Paper>

        {/* SBS/Zendesk Login Modal */}
        <Dialog open={this.state.show} onClose={this.handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            <img src={SBSLogo} alt="SBS" />
          </DialogTitle>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body2" paragraph>
              Use your SBS login information here and use a single username and password for all <strong>SBS</strong> sites.
            </Typography>

            <TextField
              fullWidth
              label="Email"
              type="text"
              name="SBSEmail"
              value={this.state.SBSEmail}
              onChange={this.handleChange("SBSEmail")}
              margin="normal"
              variant="outlined"
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              name="SBSPassword"
              value={this.state.SBSPassword}
              onChange={this.handleChange("SBSPassword")}
              margin="normal"
              variant="outlined"
            />
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={this.launchZendesk}
            >
              Login with SBS
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={this.handleClose}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }
}

export default Signin;
